// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useId } from 'react';

interface CurrencySelectorProps {
  currencies: IsoCurrencyCode[];
  value: IsoCurrencyCode;
  onChange: (value: IsoCurrencyCode) => void;
}

const CURRENCY_SELECTOR_LABEL = 'Available asset currencies';

/** Dropdown that selects which cost currency a report renders. */
export function CurrencySelector(props: CurrencySelectorProps) {
  const labelId = useId();

  const handleChange = (event: SelectChangeEvent<IsoCurrencyCode>) => {
    // MUI widens the event value to string; every option is a listed IsoCurrencyCode, so
    // parse narrows it back safely.
    props.onChange(isoCurrencyCodeSchema.parse(event.target.value));
  };

  // Clamp to a listed option so a value that is briefly out of range — before a caller
  // snaps to an available currency — doesn't trigger MUI's out-of-range warning.
  const value = props.currencies.includes(props.value)
    ? props.value
    : (props.currencies[0] ?? props.value);

  return (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id={labelId}>{CURRENCY_SELECTOR_LABEL}</InputLabel>
      <Select<IsoCurrencyCode>
        labelId={labelId}
        label={CURRENCY_SELECTOR_LABEL}
        value={value}
        onChange={handleChange}
      >
        {props.currencies.map((code) => (
          <MenuItem key={code} value={code}>
            {code}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
